/**
 * 今日の日付を YYYY-MM-DD 形式で取得
 */
export const getToday = (): string => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * 月の開始日を YYYY-MM-DD 形式で取得
 * @param month - YYYY-MM形式の月文字列
 */
export const getMonthStart = (month: string): string => {
  return `${month}-01`
}

/**
 * 月の終了日を YYYY-MM-DD 形式で取得
 * @param month - YYYY-MM形式の月文字列
 */
export const getMonthEnd = (month: string): string => {
  const [year, monthNum] = month.split('-').map(Number)
  const lastDay = new Date(year, monthNum, 0).getDate()
  return `${month}-${String(lastDay).padStart(2, '0')}`
}

/**
 * 日付文字列から年月を YYYY-MM 形式で取得
 * @param date - YYYY-MM-DD形式の日付文字列
 */
export const getYearMonth = (date: string): string => {
  return date.substring(0, 7)
}

/**
 * 現在の年月を YYYY-MM 形式で取得
 */
export const getCurrentMonth = (): string => {
  return getToday().substring(0, 7)
}

/**
 * 2つの期間が重複しているかチェック
 * @param fromDate1 - 期間1の開始日 (YYYY-MM-DD形式)
 * @param toDate1 - 期間1の終了日 (YYYY-MM-DD形式) または null (無期限)
 * @param fromDate2 - 期間2の開始日 (YYYY-MM-DD形式)
 * @param toDate2 - 期間2の終了日 (YYYY-MM-DD形式) または null (無期限)
 * @returns 重複している場合 true、重複していない場合 false
 */
export const overlap = (
  fromDate1: string,
  toDate1: string | null,
  fromDate2: string,
  toDate2: string | null,
): boolean => {
  // 期間1の終了日が期間2の開始日より前の場合、重複なし
  if (toDate1 !== null && toDate1 < fromDate2) {
    return false
  }

  // 期間2の終了日が期間1の開始日より前の場合、重複なし
  if (toDate2 !== null && toDate2 < fromDate1) {
    return false
  }

  // 上記以外の場合は重複あり
  return true
}

/**
 * Unixタイムスタンプ（秒単位）をDateオブジェクトに変換
 * @param timestamp - Unixタイムスタンプ（秒単位）
 * @returns Dateオブジェクト
 */
export const fromUnixTimestamp = (timestamp: number): Date => {
  return new Date(timestamp * 1000)
}

/**
 * 日付を YYYY-MM-DD 形式でフォーマット
 * @param date - 日付文字列またはDateオブジェクト
 * @returns YYYY-MM-DD形式の文字列
 */
export const formatDate = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * 日付を YYYY年MM月DD日 形式でフォーマット
 * @param date - 日付文字列またはDateオブジェクト
 * @returns YYYY年MM月DD日形式の文字列
 */
export const formatDateJapanese = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const year = d.getFullYear()
  const month = d.getMonth() + 1
  const day = d.getDate()
  return `${year}年${month}月${day}日`
}

/**
 * 日付を ISO 形式 (YYYY-MM-DD) でフォーマット
 * @param date - 日付文字列またはDateオブジェクト
 * @returns YYYY-MM-DD形式の文字列
 */
export const formatDateISO = (date: string | Date): string => {
  return formatDate(date)
}

/**
 * 日付と時刻を YYYY-MM-DD HH:mm:ss 形式でフォーマット
 * @param date - 日付文字列またはDateオブジェクト
 * @returns YYYY-MM-DD HH:mm:ss形式の文字列
 */
export const formatDateTime = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

/**
 * 日付をロケール形式でフォーマット (YYYY年MM月DD日)
 * @param date - 日付文字列またはDateオブジェクト
 * @returns YYYY年MM月DD日形式の文字列
 */
export const formatDateLocale = (date: string | Date): string => {
  return formatDateJapanese(date)
}

/**
 * Date オブジェクトを YYYY-MM-DD 形式の文字列に変換
 * @param date - Dateオブジェクト
 * @returns YYYY-MM-DD形式の文字列
 */
export const formatDateObject = (date: Date): string => {
  return formatDate(date)
}

/**
 * 日付文字列を ISO 形式 (YYYY-MM-DD) に変換
 * @param date - 日付文字列またはDateオブジェクト
 * @returns YYYY-MM-DD形式の文字列
 */
export const formatDateToISO = (date: string | Date): string => {
  return formatDate(date)
}

/**
 * 今日の日付を ISO 形式 (YYYY-MM-DD) で取得
 * @returns YYYY-MM-DD形式の今日の日付
 */
export const getTodayISO = (): string => {
  return getToday()
}

/**
 * 指定された日付が今日または未来かどうかを判定
 * @param date - 日付文字列またはDateオブジェクト
 * @returns 今日または未来の場合true、過去の場合false
 */
export const isTodayOrFuture = (date: string | Date): boolean => {
  const d = typeof date === 'string' ? new Date(date) : date
  const today = new Date()
  // 時刻を00:00:00にリセットして日付のみで比較
  const targetDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  )
  return targetDate >= todayDate
}

/**
 * 日付を YYYY/MM/DD 形式でフォーマット（日本語ロケール形式）
 * @param date - 日付文字列またはDateオブジェクト
 * @returns YYYY/MM/DD形式の文字列
 */
export const formatDateSlash = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const year = d.getFullYear()
  const month = d.getMonth() + 1
  const day = d.getDate()
  return `${year}/${month}/${day}`
}
